// The WhatsApp tracking assistant (15 §15.4). Pure: it reads what a person typed, says what they
// meant, and writes the reply in English or Tagalog from facts the server hands it. It never reads
// the database, so everything it decides is tested without Firebase.
//
// The server (functions/src/whatsapp) looks up who the sender is, which shipments they are a party
// to, and what the assistant was waiting for from them, then calls these functions.
import { parseBoxBarcode, parseBranchTrackingNumber } from './ids';
import { isTrackingNo } from './tracking-series';
import type { MessageLanguage } from './message-text';

export type AssistantIntent =
  | { type: 'tracking'; trackingNo: string; valid: boolean }
  | { type: 'barcode'; barcode: string }
  | { type: 'rating'; stars: number }
  | { type: 'last4'; digits: string }
  | { type: 'agent' }
  | { type: 'stop' }
  | { type: 'start' }
  | { type: 'status' }
  | { type: 'rates' }
  | { type: 'book' }
  | { type: 'season' }
  | { type: 'yes' }
  | { type: 'no' }
  | { type: 'help' }
  | { type: 'text' };

export type IntentName = AssistantIntent['type'];

const TAGALOG_WORDS = new Set([
  'po',
  'opo',
  'kumusta',
  'kamusta',
  'magkano',
  'saan',
  'nasaan',
  'padala',
  'kahon',
  'salamat',
  'hindi',
  'oo',
  'tao',
  'kausapin',
  'pasko',
  'presyo',
  'ahente',
  'tulong',
  'huwag',
  'mga',
  'ang',
  'ng',
  'naman',
  'pa',
  'ba',
  'kuya',
  'ate',
  'paki',
  'pakiusap',
]);

/** Tagalog when at least one distinctive Tagalog word is there, else the fallback. */
export function detectLanguage(text: string, fallback: MessageLanguage = 'en'): MessageLanguage {
  const words = text.toLowerCase().match(/[a-zñ]+/g) ?? [];
  return words.some((w) => TAGALOG_WORDS.has(w)) ? 'tl' : fallback;
}

const normalise = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s?/-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const TRACKING = /\b([A-Za-z]{3,4})[\s-]?(\d{9})(?:-P\d{1,2})?\b/;
/** Any other word that could be a tracking number of a series or one typed by hand (03 §3.5). */
const WORDS = /[A-Za-z0-9][A-Za-z0-9-]{2,}[A-Za-z0-9]/g;

/** A word with letters and digits that is a valid tracking number, or a piece label of one. */
function anyTrackingNo(raw: string): string | undefined {
  for (const m of raw.matchAll(WORDS)) {
    const word = m[0].toUpperCase().replace(/-P\d{1,2}$/, '');
    if (/[A-Z]/.test(word) && /\d/.test(word) && isTrackingNo(word)) return word;
  }
  return undefined;
}
const BARCODE = /\b(EB\d{10})\b/i;

/** Reads one message. A bare number or "yes" is only meaningful against what the assistant was waiting for: the server decides. */
export function parseMessage(text: string): AssistantIntent {
  const raw = text.trim();
  const t = normalise(raw);
  if (/^(stop|unsubscribe|itigil|tigil|huwag na|stop messages?|opt out)$/.test(t)) {
    return { type: 'stop' };
  }
  if (/^(start|subscribe|simula|opt in)$/.test(t)) return { type: 'start' };

  const star = /^(?:rate\s*)?([1-5])(?:\s*(?:stars?|star|\/\s*5|bituin))?$/.exec(t);
  if (star) return { type: 'rating', stars: Number(star[1]) };
  if (/^\d{4}$/.test(t)) return { type: 'last4', digits: t };
  if (/^(yes|y|yep|oo|opo|yes po|solved|resolved|ok na)$/.test(t)) return { type: 'yes' };
  if (/^(no|n|nope|hindi|hindi pa|hnd|not yet|not solved)$/.test(t)) return { type: 'no' };

  const barcode = BARCODE.exec(raw);
  if (barcode) {
    const code = (barcode[1] as string).toUpperCase();
    if (parseBoxBarcode(code)) return { type: 'barcode', barcode: code };
  }
  const tracking = TRACKING.exec(raw);
  if (tracking) {
    const trackingNo = `${(tracking[1] as string).toUpperCase()}${tracking[2] as string}`;
    return { type: 'tracking', trackingNo, valid: parseBranchTrackingNumber(trackingNo) !== null };
  }
  const other = anyTrackingNo(raw);
  if (other) return { type: 'tracking', trackingNo: other, valid: true };

  if (
    /\b(agent|human|person|representative|customer care|customer service|talk to|speak to|real person|tao|kausapin|totoong tao|ahente)\b/.test(
      t,
    )
  ) {
    return { type: 'agent' };
  }
  if (/^(status|track|tracking|my shipments?|padala ko|asan na|nasaan na)\b/.test(t)) {
    return { type: 'status' };
  }
  if (/\b(rates?|price|prices|how much|magkano|presyo|quote)\b/.test(t)) return { type: 'rates' };
  if (/\b(book|booking|pickup|pick up|kunin)\b/.test(t)) return { type: 'book' };
  if (/\b(christmas|pasko|season|cut-?off|deadline)\b/.test(t)) return { type: 'season' };
  if (/^(help|menu|hi|hello|hey|hola|kumusta|kamusta|tulong|\?)( po)?$/.test(t))
    return { type: 'help' };
  return { type: 'text' };
}

// ---- Replies ----------------------------------------------------------------------------------

type Pair = { en: string; tl: string };
const pick = (lang: MessageLanguage, p: Pair): string => p[lang];

export const HELP_MENU: Pair = {
  en: [
    'Hello from EcoBox! Send me:',
    '- your tracking number (like RUH260042171) to see where your shipment is',
    '- STATUS to see all your shipments',
    '- RATES for our prices',
    '- BOOK to book a pickup',
    '- CHRISTMAS for the cut-off dates',
    '- AGENT to talk to a person',
    '- STOP to stop messages',
  ].join('\n'),
  tl: [
    'Kumusta mula sa EcoBox! Ipadala sa akin ang:',
    '- tracking number mo (tulad ng RUH260042171) para makita kung nasaan ang padala',
    '- STATUS para sa lahat ng padala mo',
    '- RATES para sa presyo',
    '- BOOK para mag-book ng pickup',
    '- PASKO para sa mga cut-off date',
    '- TAO para makausap ang isang tao',
    '- STOP para huminto ang mga mensahe',
  ].join('\n'),
};

export const replies = {
  help: (lang: MessageLanguage): string => pick(lang, HELP_MENU),
  unknown: (lang: MessageLanguage): string =>
    pick(lang, {
      en: `Sorry, I did not understand that.\n\n${HELP_MENU.en}`,
      tl: `Pasensya na, hindi ko naintindihan iyon.\n\n${HELP_MENU.tl}`,
    }),
  stopped: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'You will not get more messages from EcoBox. Send START any time to get them again.',
      tl: 'Wala ka nang matatanggap na mensahe mula sa EcoBox. Ipadala ang START kahit kailan para matanggap muli.',
    }),
  started: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'Welcome back! You will get updates about your shipments again.',
      tl: 'Welcome back! Matatanggap mo na ulit ang mga update sa mga padala mo.',
    }),
  malformedTracking: (lang: MessageLanguage, trackingNo: string): string =>
    pick(lang, {
      en: `${trackingNo} does not look like an EcoBox tracking number. Please check it (three or four letters and nine digits).`,
      tl: `Mukhang hindi tracking number ng EcoBox ang ${trackingNo}. Pakisuri po (tatlo o apat na letra at siyam na numero).`,
    }),
  askLast4: (lang: MessageLanguage, trackingNo: string): string =>
    pick(lang, {
      en: `To show ${trackingNo}, please send the last 4 digits of the consignee's phone number.`,
      tl: `Para ipakita ang ${trackingNo}, ipadala ang huling 4 na numero ng phone number ng tatanggap.`,
    }),
  notFound: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'We could not find a shipment with those details. Please check the tracking number and the last 4 digits, or send AGENT to talk to a person.',
      tl: 'Wala kaming nakitang padala sa mga detalyeng iyon. Pakisuri ang tracking number at ang huling 4 na numero, o ipadala ang TAO para makausap ang isang tao.',
    }),
  refuse: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'Sorry, we cannot show this shipment to this number. Please ask the sender for the tracking link, or send AGENT to talk to a person.',
      tl: 'Pasensya na, hindi namin maipapakita ang padalang ito sa numerong ito. Hingin po sa nagpadala ang tracking link, o ipadala ang TAO para makausap ang isang tao.',
    }),
  tooManyTries: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'Too many tries. Please wait a few minutes, or send AGENT to talk to a person.',
      tl: 'Masyadong maraming subok. Maghintay ng ilang minuto, o ipadala ang TAO para makausap ang isang tao.',
    }),
  noShipments: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'We do not see any shipments for this number yet. Send a tracking number, or BOOK to book a pickup.',
      tl: 'Wala pa kaming nakikitang padala para sa numerong ito. Ipadala ang tracking number, o BOOK para mag-book ng pickup.',
    }),
  agentOpened: (lang: MessageLanguage, ticketId: string): string =>
    pick(lang, {
      en: `Thank you. We opened a case (${ticketId}) and a person from customer care will contact you within 24 hours.`,
      tl: `Salamat po. Nagbukas kami ng kaso (${ticketId}) at makikipag-ugnayan sa iyo ang isang tao mula sa customer care sa loob ng 24 oras.`,
    }),
  askComment: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'Thank you. Tell us what went wrong in one message, and add a photo if you have one.',
      tl: 'Salamat po. Sabihin sa isang mensahe kung ano ang nangyari, at magdagdag ng litrato kung mayroon.',
    }),
  ratingThanks: (lang: MessageLanguage, reviewUrl: string | null): string =>
    pick(lang, {
      en: reviewUrl
        ? `Thank you for your rating! If you have a minute, a public review helps other families: ${reviewUrl}`
        : 'Thank you for your rating! We are glad you are happy with EcoBox.',
      tl: reviewUrl
        ? `Salamat sa rating mo! Kung may oras ka, makakatulong sa ibang pamilya ang public review: ${reviewUrl}`
        : 'Salamat sa rating mo! Natutuwa kaming masaya ka sa EcoBox.',
    }),
  ratingTicket: (lang: MessageLanguage, ticketId: string): string =>
    pick(lang, {
      en: `We are sorry it was not a good experience. We opened a case (${ticketId}); customer care will contact you within 24 hours. You can tell us more in your next message.`,
      tl: `Pasensya na po sa hindi magandang karanasan. Nagbukas kami ng kaso (${ticketId}); makikipag-ugnayan ang customer care sa loob ng 24 oras. Maaari mo kaming sabihan pa sa susunod mong mensahe.`,
    }),
  commentAdded: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'Thank you, we added that to your case.',
      tl: 'Salamat, idinagdag namin iyan sa kaso mo.',
    }),
  ratingNoContext: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'Thank you! I am not sure which shipment that number is for. Send the tracking number, or AGENT to talk to a person.',
      tl: 'Salamat! Hindi ko matiyak kung para saan ang numerong iyon. Ipadala ang tracking number, o TAO para makausap ang isang tao.',
    }),
  followupYes: (lang: MessageLanguage): string =>
    pick(lang, {
      en: 'Great, we are glad it is solved. Thank you for your patience.',
      tl: 'Mabuti, natutuwa kaming nalutas na. Salamat sa pasensya mo.',
    }),
  followupNo: (lang: MessageLanguage, ticketId: string): string =>
    pick(lang, {
      en: `Sorry about that. We reopened your case (${ticketId}) and customer care will contact you again.`,
      tl: `Pasensya na po. Binuksan namin muli ang kaso mo (${ticketId}) at makikipag-ugnayan ulit ang customer care.`,
    }),
  rates: (lang: MessageLanguage, url: string): string =>
    pick(lang, {
      en: `Our prices depend on the box size and where it is going. Get an estimate here: ${url}`,
      tl: `Depende sa laki ng kahon at destinasyon ang presyo. Kumuha ng estimate dito: ${url}`,
    }),
  book: (lang: MessageLanguage, url: string): string =>
    pick(lang, {
      en: `You can book a pickup here: ${url}`,
      tl: `Maaari kang mag-book ng pickup dito: ${url}`,
    }),
  season: (
    lang: MessageLanguage,
    seasons: readonly { name: string; lines: readonly string[] }[],
  ): string => {
    if (seasons.length === 0) {
      return pick(lang, {
        en: 'There is no season with cut-off dates right now. Send RATES or BOOK for the latest.',
        tl: 'Walang season na may cut-off date sa ngayon. Ipadala ang RATES o BOOK para sa pinakabago.',
      });
    }
    const head = pick(lang, { en: 'Last collection dates:', tl: 'Huling araw ng koleksyon:' });
    return [head, ...seasons.flatMap((s) => [`${s.name}:`, ...s.lines.map((l) => `- ${l}`)])].join(
      '\n',
    );
  },
};

// ---- Tracking replies --------------------------------------------------------------------------

export interface TrackingFacts {
  trackingNo: string;
  statusLabel: string;
  destinationCity: string;
  boxCount: number;
  lastMilestone: { label: string; at: string } | null;
  hoursInState: number | null;
  predicted: { from: string; to: string } | null;
  trackUrl: string | null;
}

function duration(hours: number, lang: MessageLanguage): string {
  if (hours < 1) return lang === 'tl' ? 'wala pang isang oras' : 'less than an hour';
  if (hours < 48) return lang === 'tl' ? `${hours} oras` : `${hours} hour(s)`;
  const days = Math.floor(hours / 24);
  return lang === 'tl' ? `${days} araw` : `${days} day(s)`;
}

/** Status, last milestone, time in state, predicted window and the link (15 §15.4). */
export function trackingReply(facts: TrackingFacts, lang: MessageLanguage): string {
  const lines: string[] =
    lang === 'tl'
      ? [`Padala ${facts.trackingNo} (${facts.boxCount} kahon papuntang ${facts.destinationCity})`]
      : [`Shipment ${facts.trackingNo} (${facts.boxCount} box(es) to ${facts.destinationCity})`];
  lines.push(`${lang === 'tl' ? 'Katayuan' : 'Status'}: ${facts.statusLabel}`);
  if (facts.lastMilestone) {
    lines.push(
      `${lang === 'tl' ? 'Huling hakbang' : 'Last step'}: ${facts.lastMilestone.label} (${facts.lastMilestone.at.slice(0, 10)})`,
    );
  }
  if (facts.hoursInState !== null) {
    lines.push(
      `${lang === 'tl' ? 'Nasa yugtong ito nang' : 'Time in this step'}: ${duration(facts.hoursInState, lang)}`,
    );
  }
  if (facts.predicted) {
    lines.push(
      `${lang === 'tl' ? 'Inaasahang dating' : 'Expected delivery'}: ${facts.predicted.from} - ${facts.predicted.to}`,
    );
  }
  if (facts.trackUrl)
    lines.push(`${lang === 'tl' ? 'Buong detalye' : 'Full details'}: ${facts.trackUrl}`);
  return lines.join('\n');
}

export function statusListReply(
  items: readonly { trackingNo: string; statusLabel: string; destinationCity: string }[],
  lang: MessageLanguage,
): string {
  if (items.length === 0) return replies.noShipments(lang);
  const head = lang === 'tl' ? 'Ang mga padala mo:' : 'Your shipments:';
  const shown = items.slice(0, 8);
  const more =
    items.length > shown.length
      ? [
          lang === 'tl'
            ? `...at ${items.length - shown.length} pa`
            : `...and ${items.length - shown.length} more`,
        ]
      : [];
  return [
    head,
    ...shown.map((i) => `${i.trackingNo} - ${i.statusLabel} (${i.destinationCity})`),
    ...more,
    lang === 'tl'
      ? 'Ipadala ang tracking number para sa buong detalye.'
      : 'Send a tracking number for the full details.',
  ].join('\n');
}

// ---- Who may see a shipment -------------------------------------------------------------------

export type TrackAccess = 'party' | 'ask_last4' | 'refuse';

/**
 * The sender's number is a party to the shipment: show it. Otherwise ask for the last 4 digits,
 * unless the sender has already failed too often, in which case refuse politely (15 §15.4).
 */
export function trackAccess(
  isParty: boolean,
  recentFailures: number,
  maxFailures = 5,
): TrackAccess {
  if (isParty) return 'party';
  return recentFailures >= maxFailures ? 'refuse' : 'ask_last4';
}
